import React from 'react';
import {
    Container,
    CaseLink
} from '../StyledComponents/StyledComponents';
import { ClientText } from "./Style";
import Button from '../Utility/Button';
import CategoryDisplay from './CategoryDisplay';
import { Fade } from 'react-awesome-reveal';
import styled from 'styled-components';

// Estilo personalizado para el botón en las tarjetas
const ButtonContainer = styled.div`
    margin-top: ${props => props.compact ? '0' : '0.5rem'};
    margin-bottom: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    
    p {
        margin: 0;
        text-transform: uppercase;
        font-size: 12px;
        letter-spacing: 1px;
        color: ${props => props.theme.body.text};
        opacity: 0.7;
        font-weight: 500;
    }
`;

// Botón compacto para las tarjetas
const CompactButton = styled.button`
    font-family: ${props => props.theme.typography.font};
    font-size: 13px;
    border: none;
    color: ${props => props.theme.body.headers};
    transition: ${props => props.theme.transition};
    font-weight: 500;
    letter-spacing: 1px;
    display: inline-block;
    vertical-align: middle;
    cursor: pointer !important;
    outline: none;
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    text-transform: uppercase;

    i {
        transition: ${props => props.theme.transition};
        opacity: 0.25;
        margin-left: 0.5rem;
    }

    &:hover {
        i {
            margin-left: 0.75rem;
            opacity: 1;
        }
    }
`;

// Nuevo estilo oficial del blog basado en CardStyle5
const ArticleCard = styled.div`
    background: white;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 1rem;
    box-shadow: 0 2px 15px rgba(0,0,0,0.08);
    border: 1px solid #f0f0f0;
    transition: box-shadow 0.3s ease;
    height: ${props => props.featured ? 'auto' : 'auto'};
    min-height: ${props => props.featured ? 'auto' : '480px'};
    display: ${props => props.featured ? 'flex' : 'flex'};
    flex-direction: ${props => props.featured ? 'row' : 'column'};
    
    @media (max-width: 768px) {
        height: ${props => props.featured ? 'auto' : 'auto'};
        min-height: ${props => props.featured ? '400px' : '440px'};
        flex-direction: column;
    }
    
    &:hover {
        box-shadow: 0 8px 25px rgba(0,0,0,0.12);
        
        .image-container img {
            transform: scale(1.02);
        }
    }
    
    .image-container {
        width: ${props => props.featured ? '50%' : '100%'};
        height: ${props => props.featured ? '100%' : '260px'};
        overflow: hidden;
        position: relative;
        order: ${props => props.featured ? '2' : '0'};
        flex-shrink: 0;
        
        @media (max-width: 768px) {
            width: 100%;
            height: ${props => props.featured ? '200px' : '240px'};
        }
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
        }
        
        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%);
        }
    }
    
    .content {
        padding: ${props => props.featured ? '2rem' : '0 1.5rem 0.5rem 1.5rem'};
        width: ${props => props.featured ? '50%' : '100%'};
        order: ${props => props.featured ? '1' : '0'};
        display: flex;
        flex-direction: column;
        justify-content: ${props => props.featured ? 'center' : 'space-between'};
        box-sizing: border-box;
        overflow: hidden;
        max-width: 100%;
        flex: ${props => props.featured ? 'none' : '1'};
        
        @media (max-width: 768px) {
            width: 100%;
            padding: ${props => props.featured ? '1.5rem' : '0 1.25rem 0.5rem 1.25rem'};
        }
    }
    
    h3 {
        color: ${props => props.theme.body.headers};
        margin-bottom: ${props => props.featured ? '1rem' : '0.1rem'};
        font-size: 1.2rem;
        line-height: 1.4;
        font-weight: 600;
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: auto;
        max-width: 100%;
        box-sizing: border-box;
        flex: ${props => props.featured ? 'none' : '1'};
        display: ${props => props.featured ? 'block' : '-webkit-box'};
        -webkit-line-clamp: ${props => props.featured ? 'none' : '6'};
        -webkit-box-orient: ${props => props.featured ? 'initial' : 'vertical'};
        overflow: ${props => props.featured ? 'visible' : 'hidden'};
        
        .title {
            font-family: ${props => props.theme.typography.headerFont};
            font-weight: 700;
            display: inline;
        }
        
        .abstract {
            font-family: ${props => props.theme.typography.font};
            font-weight: 400;
            display: inline;
            opacity: 0.8;
            font-size: 0.9em;
        }
    }
`;

const FeaturedArticle = (props) => {
    // Validación para evitar errores si post no está definido
    if (!props.post) {
        return null;
    }

    return (
        <Fade direction="top" duration={1000} triggerOnce cascade>
            <Container top={props.featured ? props.top : 0}>
                <CaseLink href={'/' + props.post.route}>
                    <ArticleCard featured={props.featured}>
                        <div className="image-container">
                            <img src={props.post.image} alt={props.post.title} />
                        </div>
                        <div className="content">
                            <CategoryDisplay 
                                category={props.post.category}
                            />
                            <h3>
                                <span className="title">{props.post.title}</span>
                                <span> — </span>
                                <span className="abstract">{props.post.description}</span>
                            </h3>
                            <ButtonContainer compact={!props.featured}>
                                {props.featured ? (
                                    <Button
                                        text="Ver Más"
                                        right={true}
                                        tabIndex={-1}
                                    />
                                ) : (
                                    <CompactButton>
                                        <span>Ver Más</span>
                                        <i className="las la-arrow-right"></i>
                                    </CompactButton>
                                )}
                            </ButtonContainer>
                        </div>
                    </ArticleCard>
                </CaseLink>
            </Container>
        </Fade>
    );
};

export default FeaturedArticle;
